import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Navigate while respecting the app's unsaved-changes guard.
 *
 * The guard (`useUnsavedChanges`) can't actually stop react-router — it only
 * listens for a `navigationStart` CustomEvent and, when the page is dirty,
 * synchronously dispatches `navigationCancelled`. So every in-app navigation
 * that must be guarded has to (1) announce itself via that event carrying the
 * target url and (2) abort when cancelled, handing the url to the guard's modal
 * (via `detail.url`). Plain `<Link>` / `navigate()` bypass the guard entirely
 * and silently discard unsaved edits.
 *
 * This mirrors the sidebar's `navigateTo` (see `useEventSidebar`) so builder
 * breadcrumbs get the same protection. DOM `dispatchEvent` is synchronous, so
 * the cancel flag is reliable immediately after it returns — no setTimeout.
 */
export function useGuardedNavigate() {
  const navigate = useNavigate();

  return useCallback(
    (url: string) => {
      let cancelled = false;
      const onCancel = () => {
        cancelled = true;
      };

      window.addEventListener("navigationCancelled", onCancel, { once: true });
      window.dispatchEvent(
        new CustomEvent("navigationStart", { detail: { url } }),
      );
      window.removeEventListener("navigationCancelled", onCancel);

      if (!cancelled) navigate(url);
    },
    [navigate],
  );
}
